import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import BrandMark from './BrandMark';
import { 
  FiZap, FiMap, FiGitBranch, FiLink, FiCheckCircle, 
  FiAlertCircle, FiInbox, FiFilter, FiPlus,
  FiChevronRight, FiChevronLeft, FiHelpCircle,
  FiLayout, FiCpu, FiUsers, FiMousePointer, FiShare2
} from 'react-icons/fi';

const DemoView = () => {
  const [activeTab, setActiveTab] = useState('overview'); 
  const [currentStep, setCurrentStep] = useState(0);
  const [simState, setSimState] = useState('idle');

  const workflowSteps = [
    {
      title: "Omni-Capture",
      desc: "Founder's initial spark: 'Subscription model for sustainable fashion.' Capture fragments instantly via mobile or command palette.",
      icon: FiZap,
      color: "text-maple bg-maple-soft",
      preview: (
        <div className="w-full h-full flex flex-col p-6 gap-4">
          <div className="h-10 w-full bg-white rounded-xl shadow-sm border border-paper-dark flex items-center px-4 gap-2">
            <div className="w-2 h-2 rounded-full bg-maple animate-pulse" />
            <div className="h-2 w-32 bg-paper-dark rounded" />
          </div>
          <div className="flex-1 bg-white rounded-2xl shadow-lg border border-paper-dark p-4">
            <div className="h-3 w-3/4 bg-paper-dark rounded mb-3" />
            <div className="h-3 w-1/2 bg-paper-dark rounded mb-3" />
            <div className="h-16 w-full bg-maple-soft/30 rounded-xl" />
          </div>
        </div>
      )
    },
    {
      title: "Spatial Anchoring",
      desc: "Notes aren't buried in lists. They are dropped onto an infinite canvas where proximity implies relationship.",
      icon: FiMap,
      color: "text-blue-600 bg-blue-50",
      preview: (
        <div className="w-full h-full relative p-6">
          <div className="absolute top-10 left-10 w-24 h-24 bg-white border-2 border-blue-200 rounded-xl shadow-md rotate-3" />
          <div className="absolute bottom-12 right-12 w-28 h-20 bg-white border-2 border-blue-200 rounded-xl shadow-md -rotate-6" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full shadow-lg shadow-blue-500/50" />
        </div>
      )
    },
    {
      title: "Knowledge Branching",
      desc: "Student adds research: 'Competitor pricing matrix' and 'Material sourcing'. New cards branch off visually.",
      icon: FiGitBranch,
      color: "text-purple-600 bg-purple-50",
      preview: (
        <div className="w-full h-full p-6 flex flex-col items-center justify-center">
          <div className="w-16 h-16 bg-white border-2 border-purple-200 rounded-xl shadow-sm z-10" />
          <div className="flex gap-12 -mt-2">
             <div className="w-12 h-12 bg-white border border-purple-100 rounded-lg shadow-sm" />
             <div className="w-12 h-12 bg-white border border-purple-100 rounded-lg shadow-sm" />
          </div>
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <path d="M150 150 L100 200 M150 150 L200 200" stroke="#E9D5FF" strokeWidth="2" strokeDasharray="4" strokeLinecap="round" />
          </svg>
        </div>
      )
    },
    {
      title: "Semantic Linking",
      desc: "Writer connects 'Sustainability' to 'Manufacturing Costs'. The Graph Engine visualizes these cross-references.",
      icon: FiLink,
      color: "text-ink bg-paper-dark",
      preview: (
        <div className="w-full h-full p-6 flex items-center justify-center">
          <div className="relative w-40 h-40 border-2 border-dashed border-ink/10 rounded-full flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-ink absolute top-0" />
            <div className="w-8 h-8 rounded-full bg-maple absolute bottom-4 right-0" />
            <div className="w-8 h-8 rounded-full bg-blue-500 absolute bottom-4 left-0" />
            <svg className="absolute inset-0 w-full h-full">
              <line x1="80" y1="20" x2="140" y2="140" stroke="#0F172A" strokeWidth="1" strokeDasharray="2" />
              <line x1="80" y1="20" x2="20" y2="140" stroke="#0F172A" strokeWidth="1" strokeDasharray="2" />
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Synthesis & Pitch",
      desc: "Convert messy clusters into a structured project folder or a high-fidelity presentation dashboard.",
      icon: FiCheckCircle,
      color: "text-green-600 bg-green-50",
      preview: (
        <div className="w-full h-full p-6 flex flex-col gap-3">
          <div className="h-8 w-full bg-green-100 rounded-lg flex items-center px-3 justify-between">
            <div className="h-2 w-20 bg-green-600/30 rounded" />
            <FiCheckCircle className="text-green-600 w-3 h-3" />
          </div>
          <div className="flex-1 grid grid-cols-2 gap-2">
            <div className="bg-white border rounded-lg shadow-sm" />
            <div className="bg-white border rounded-lg shadow-sm" />
            <div className="bg-white border rounded-lg shadow-sm" />
            <div className="bg-white border rounded-lg shadow-sm" />
          </div>
        </div>
      )
    }
  ];

  const faqs = [
    { q: "How do I start my first project?", a: "Simply hit 'Cmd + K' or use the floating '+' button to drop your first note anywhere on the infinite canvas." },
    { q: "Is my data stored during this demo?", a: "No. This is a local-only simulated environment. No private data is sent to external servers." },
    { q: "Can I collaborate with others?", a: "In the full version, MindMaple supports real-time multiplayer spatial editing for teams and classrooms." },
    { q: "What problem does this solve for writers?", a: "Linear documents often stifle non-linear thinking. MindMaple lets you see connections you'd miss in a standard text editor." },
    { q: "Does it support mobile devices?", a: "Yes, MindMaple is fully responsive, allowing you to capture ideas on the go and organize them on the desktop." }
  ];

  const sampleData = [
    { id: 1, name: "Series A Narrative", type: "Canvas", nodes: 34, status: "Active", date: "Oct 24, 2024" },
    { id: 2, name: "Thesis Research", type: "Graph", nodes: 212, status: "Review", date: "Oct 22, 2024" },
    { id: 3, name: "Character Arc Map", type: "Note", nodes: 15, status: "Draft", date: "Oct 20, 2024" },
    { id: 4, name: "Growth Loop v2", type: "Canvas", nodes: 56, status: "Active", date: "Oct 18, 2024" },
    { id: 5, name: "User Interview Logs", type: "Folder", nodes: 8, status: "Closed", date: "Oct 15, 2024" },
  ];

  return (
    <div className="h-full flex flex-col bg-paper overflow-y-auto pb-12 hide-scrollbar">
      {/* Header */}
      <div className="px-4 md:px-8 pt-8 pb-6 bg-paper-light border-b border-paper-dark sticky top-0 z-10 backdrop-blur-md">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BrandMark className="w-5 h-5" />
              <span className="text-[10px] font-black text-maple uppercase tracking-widest">Product Hub</span>
            </div>
            <h1 className="text-2xl font-black text-ink mb-1">Ecosystem Overview</h1>
            <p className="text-ink-muted text-sm max-w-2xl font-medium">
              A spatial canvas for founders, researchers, and writers to synthesize chaotic information.
            </p>
          </div>
        </div>

        <div className="flex gap-2 md:gap-4 mt-6 overflow-x-auto pb-2 no-scrollbar">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'flow', label: 'How It Works' },
            { id: 'data', label: 'Sample Data' },
            { id: 'states', label: 'System States' },
            { id: 'faq', label: 'FAQ' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-xs md:text-sm font-bold whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-ink text-white shadow-lg shadow-ink/10' 
                  : 'bg-white text-ink-muted hover:text-ink border border-paper-dark shadow-sm'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 md:px-8 mt-8 max-w-6xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {/* Overview Section */}
          {activeTab === 'overview' && (
            <motion.div 
              key="overview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <div className="md:col-span-2 space-y-6">
                <div className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-paper-dark shadow-sm relative overflow-hidden group">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-black text-ink mb-6 leading-tight">Spatial Thought Synthesis</h2>
                    <p className="text-ink-light text-lg md:text-xl leading-relaxed mb-8 font-medium opacity-80">
                      MindMaple solves the "linear constraint" problem. We provide an infinite coordinate-based canvas where proximity implies meaning and links create knowledge.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-start gap-4 p-5 bg-paper rounded-3xl border border-paper-dark/50">
                        <div className="bg-maple text-white p-2.5 rounded-xl shadow-lg shadow-maple/20"><SafeIcon icon={FiCpu} /></div>
                        <div>
                          <h4 className="font-black text-xs uppercase tracking-widest text-ink mb-1">The Problem</h4>
                          <p className="text-xs text-ink-muted leading-relaxed">Document folders hide connections. Linear lists obscure the "big picture" needed for complex architecture.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 p-5 bg-ink text-white rounded-3xl">
                        <div className="bg-white/10 p-2.5 rounded-xl"><SafeIcon icon={FiZap} className="text-maple" /></div>
                        <div>
                          <h4 className="font-black text-xs uppercase tracking-widest text-white/60 mb-1 text-maple">The Outcome</h4>
                          <p className="text-xs text-white/70 leading-relaxed">A visual brain-map that turns fragments into frameworks, ready for pitch, publication, or execution.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Abstract Background Decoration */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-maple-soft/20 rounded-full blur-3xl -mr-32 -mt-32" />
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white border border-paper-dark p-8 rounded-[2rem] shadow-sm">
                  <h3 className="font-black text-xs uppercase tracking-[0.2em] text-ink-muted mb-6">User Personas</h3>
                  <div className="space-y-5">
                    {[
                      { icon: FiUsers, label: "Founders", sub: "Pitch Narratives" },
                      { icon: FiCheckCircle, label: "Researchers", sub: "Data Synthesis" },
                      { icon: FiLayout, label: "Writers", sub: "Lore Mapping" }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4 group">
                        <div className="w-12 h-12 bg-paper rounded-2xl flex items-center justify-center text-ink group-hover:bg-maple-soft group-hover:text-maple transition-all border border-paper-dark">
                          <SafeIcon icon={item.icon} className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-black text-sm text-ink">{item.label}</p>
                          <p className="text-[10px] font-bold text-ink-muted uppercase tracking-widest">{item.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* How It Works */}
          {activeTab === 'flow' && (
            <motion.div 
              key="flow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              <div className="flex items-center gap-3 px-2 overflow-x-auto no-scrollbar pb-2">
                {workflowSteps.map((step, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setCurrentStep(idx)}
                    className={`flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-2xl text-xs font-black transition-all border ${
                      currentStep === idx 
                        ? 'bg-maple text-white border-maple shadow-xl shadow-maple/20 -translate-y-1' 
                        : 'bg-white text-ink-muted border-paper-dark hover:border-ink/20'
                    }`}
                  >
                    <span className="opacity-50">0{idx + 1}</span>
                    <span className="whitespace-nowrap">{step.title}</span>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center bg-white p-6 md:p-12 rounded-[3rem] border border-paper-dark shadow-sm overflow-hidden">
                <div className="order-2 lg:order-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-maple-soft text-maple rounded-full text-[10px] font-black uppercase tracking-widest mb-6">
                    Stage {currentStep + 1}
                  </div>
                  <h2 className="text-4xl md:text-5xl font-black text-ink mb-6 tracking-tight">{workflowSteps[currentStep].title}</h2>
                  <p className="text-ink-light leading-relaxed mb-10 text-xl font-medium opacity-80">
                    {workflowSteps[currentStep].desc}
                  </p>
                  
                  <div className="flex gap-4">
                    <button 
                      disabled={currentStep === 0}
                      onClick={() => setCurrentStep(prev => prev - 1)}
                      className="p-5 rounded-2xl border border-paper-dark hover:bg-paper disabled:opacity-30 transition-all active:scale-95"
                    >
                      <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
                    </button>
                    <button 
                      onClick={() => setCurrentStep(prev => (prev + 1) % workflowSteps.length)}
                      className="flex-1 py-5 px-8 bg-ink text-white rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-ink-light transition-all shadow-2xl shadow-ink/20 active:scale-[0.98]"
                    >
                      {currentStep === workflowSteps.length - 1 ? 'Finish Journey' : 'Next Stage'}
                      <SafeIcon icon={FiChevronRight} />
                    </button>
                  </div>
                </div>

                <div className="order-1 lg:order-2 relative aspect-square bg-paper border border-paper-dark rounded-[2.5rem] shadow-inner overflow-hidden flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    <motion.div 
                      key={currentStep}
                      initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                      exit={{ opacity: 0, scale: 1.1 }}
                      className="w-full h-full relative"
                    >
                      {workflowSteps[currentStep].preview}
                      <div className="absolute inset-0 bg-dot-pattern opacity-20 pointer-events-none" />
                    </motion.div>
                  </AnimatePresence>
                  {/* Floating Cursor Simulation */}
                  <motion.div 
                    animate={{ x: [0, 20, -20, 0], y: [0, -30, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 8 }}
                    className="absolute z-20 top-1/2 left-1/2 text-maple drop-shadow-lg"
                  >
                    <SafeIcon icon={FiMousePointer} className="w-8 h-8 fill-current" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Sample Data */}
          {activeTab === 'data' && (
            <motion.div 
              key="data"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-[2rem] border border-paper-dark shadow-sm overflow-hidden"
            >
              <div className="p-8 border-b border-paper-dark flex flex-col sm:flex-row justify-between items-center bg-paper-light/50 gap-6">
                <div className="relative w-full sm:w-96">
                  <SafeIcon icon={FiFilter} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
                  <input type="text" placeholder="Search knowledge clusters..." className="w-full pl-12 pr-4 py-3 bg-white border border-paper-dark rounded-2xl text-sm font-bold focus:ring-4 ring-maple/10 outline-none transition-all shadow-sm" />
                </div>
                <button className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-3.5 bg-maple text-white rounded-2xl text-sm font-black hover:bg-maple-light transition-all shadow-xl shadow-maple/20 active:scale-95">
                  <SafeIcon icon={FiPlus} /> New Idea Node
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[700px]">
                  <thead>
                    <tr className="bg-paper text-ink-muted text-[10px] uppercase tracking-[0.3em] font-black border-b border-paper-dark">
                      <th className="px-10 py-6">Knowledge Asset</th>
                      <th className="px-10 py-6">Format</th>
                      <th className="px-10 py-6 text-center">Nodes</th>
                      <th className="px-10 py-6">Status</th>
                      <th className="px-10 py-6">Modified</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-paper-dark/50">
                    {sampleData.map((row) => (
                      <tr key={row.id} className="hover:bg-paper/50 transition-colors cursor-pointer group">
                        <td className="px-10 py-8">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${row.status === 'Active' ? 'bg-maple-soft text-maple' : 'bg-paper text-ink-muted'} border border-paper-dark shadow-sm`}>
                               <BrandMark className="w-5 h-5" variant={row.status === 'Active' ? 'default' : 'ink'} />
                            </div>
                            <span className="font-black text-ink group-hover:text-maple transition-colors">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-10 py-8">
                          <span className="flex items-center gap-2 text-xs font-bold text-ink-muted uppercase tracking-widest">
                            <SafeIcon icon={row.type === 'Canvas' ? FiMap : row.type === 'Graph' ? FiShare2 : FiLayout} className="w-4 h-4 opacity-40" />
                            {row.type}
                          </span>
                        </td>
                        <td className="px-10 py-8 text-sm text-ink-muted text-center font-mono font-bold">{row.nodes}</td>
                        <td className="px-10 py-8">
                          <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] border ${
                            row.status === 'Active' ? 'bg-green-50 text-green-700 border-green-100' :
                            row.status === 'Draft' ? 'bg-paper-dark text-ink-muted border-paper-dark' :
                            'bg-orange-50 text-orange-700 border-orange-100'
                          }`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-10 py-8 text-xs font-bold text-ink-muted/60">{row.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Simulated States */}
          {activeTab === 'states' && (
            <motion.div 
              key="states"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              <div className="bg-white p-10 rounded-[2.5rem] border border-paper-dark shadow-sm">
                <div className="flex items-center gap-3 mb-10">
                  <BrandMark className="w-6 h-6" />
                  <h3 className="text-xs font-black text-ink-muted uppercase tracking-[0.2em] leading-none">System Engine Simulation</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
                  {['idle', 'loading', 'success', 'error', 'empty'].map(s => (
                    <button 
                      key={s}
                      onClick={() => setSimState(s)}
                      className={`px-6 py-5 rounded-[1.25rem] border capitalize transition-all text-sm font-black ${
                        simState === s 
                          ? 'bg-ink text-white border-ink shadow-2xl shadow-ink/30 -translate-y-1' 
                          : 'bg-white text-ink-muted border-paper-dark hover:border-ink/20 hover:bg-paper/30'
                      }`}
                    >
                      {s} state
                    </button>
                  ))}
                </div>
                <div className="p-6 bg-paper rounded-2xl border-2 border-dashed border-paper-dark text-xs text-ink-muted leading-relaxed font-medium">
                  <span className="font-black text-ink block mb-2 uppercase tracking-widest text-[10px]">Developer Interface:</span>
                  Toggle the protocols above to observe how MindMaple's spatial engine handles asynchronous link processing and error recovery.
                </div>
              </div>

              <div className="bg-white p-12 rounded-[2.5rem] border border-paper-dark shadow-sm flex items-center justify-center min-h-[420px] relative overflow-hidden group">
                <AnimatePresence mode="wait">
                  {simState === 'idle' && (
                    <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center relative z-10">
                      <div className="w-24 h-24 bg-paper-dark/30 rounded-[2rem] mx-auto mb-8 flex items-center justify-center text-ink-muted border border-paper-dark">
                        <SafeIcon icon={FiInbox} className="w-12 h-12" />
                      </div>
                      <p className="font-black text-2xl text-ink tracking-tight">Handshake Ready</p>
                      <p className="text-sm font-medium text-ink-muted mt-3">Monitoring neural link activity...</p>
                    </motion.div>
                  )}
                  {simState === 'loading' && (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center relative z-10">
                      <motion.div 
                        animate={{ rotate: 360, scale: [1, 1.1, 1] }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                        className="w-24 h-24 border-8 border-maple/10 border-t-maple rounded-full mx-auto mb-8 shadow-xl"
                      />
                      <p className="font-black text-ink tracking-[0.4em] uppercase text-xs">Calibrating Nodes</p>
                    </motion.div>
                  )}
                  {simState === 'success' && (
                    <motion.div key="success" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center relative z-10">
                      <div className="w-24 h-24 bg-green-50 text-green-600 rounded-full mx-auto mb-8 flex items-center justify-center shadow-2xl shadow-green-200/50 border-4 border-white">
                        <SafeIcon icon={FiCheckCircle} className="w-12 h-12" />
                      </div>
                      <p className="font-black text-2xl text-ink tracking-tight">Sync Complete</p>
                      <div className="mt-5 px-6 py-2.5 bg-green-50 text-green-700 text-[10px] font-black uppercase tracking-widest rounded-full border border-green-100 italic">2.4k Nodes Anchored</div>
                    </motion.div>
                  )}
                  {simState === 'error' && (
                    <motion.div key="error" initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="text-center relative z-10">
                      <div className="w-24 h-24 bg-red-50 text-red-600 rounded-full mx-auto mb-8 flex items-center justify-center shadow-2xl shadow-red-200/50 border-4 border-white">
                        <SafeIcon icon={FiAlertCircle} className="w-12 h-12" />
                      </div>
                      <p className="font-black text-2xl text-red-600 tracking-tight">Protocol Interrupted</p>
                      <p className="text-sm font-medium text-ink-muted mt-3">Link signal degraded. Handshake timeout.</p>
                      <button onClick={() => setSimState('loading')} className="mt-8 px-8 py-3 bg-red-600 text-white rounded-2xl text-xs font-black shadow-xl shadow-red-200 transition-transform active:scale-95">RESTART HANDSHAKE</button>
                    </motion.div>
                  )}
                  {simState === 'empty' && (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center relative z-10">
                      <div className="w-40 h-56 bg-paper rounded-[2rem] mx-auto mb-8 flex flex-col items-center justify-center gap-4 border-4 border-dashed border-paper-dark relative shadow-inner">
                         <BrandMark className="w-12 h-12 opacity-10" variant="ink" />
                         <div className="space-y-2">
                           <div className="w-16 h-2 bg-paper-dark rounded" />
                           <div className="w-24 h-2 bg-paper-dark rounded" />
                         </div>
                      </div>
                      <p className="text-xl font-black text-ink-muted">Coordinates Vacant</p>
                      <button className="mt-6 px-10 py-3.5 bg-maple text-white rounded-2xl text-sm font-black hover:bg-maple-light transition-all shadow-2xl shadow-maple/30 active:scale-95">Drop Anchor Node</button>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="absolute inset-0 pointer-events-none bg-dot-pattern opacity-10" />
              </div>
            </motion.div>
          )}

          {/* FAQ */}
          {activeTab === 'faq' && (
            <motion.div 
              key="faq"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-3xl mx-auto space-y-6"
            >
              <div className="text-center mb-12">
                <BrandMark className="w-12 h-12 mx-auto mb-6" />
                <h2 className="text-4xl font-black text-ink tracking-tight">Support Engine</h2>
                <p className="text-ink-muted mt-3 font-medium">Navigating the MindMaple spatial ecosystem.</p>
              </div>
              
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-white rounded-[1.5rem] border border-paper-dark transition-all duration-300 open:shadow-2xl open:shadow-ink/5">
                  <summary className="flex items-center justify-between p-8 cursor-pointer list-none">
                    <span className="font-black text-ink pr-6 text-lg">{faq.q}</span>
                    <div className="w-8 h-8 rounded-full bg-paper flex items-center justify-center text-ink-muted group-open:rotate-180 transition-transform">
                      <SafeIcon icon={FiChevronRight} className="rotate-90 w-5 h-5" />
                    </div>
                  </summary>
                  <div className="px-8 pb-8 text-ink-light leading-relaxed font-medium border-t border-paper-dark pt-6 text-base opacity-80">
                    {faq.a}
                  </div>
                </details>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Credit */}
      <div className="mt-auto px-8 py-20 text-center">
        <div className="inline-flex flex-col items-center gap-4">
          <div className="flex items-center gap-2 opacity-50 grayscale hover:grayscale-0 hover:opacity-100 transition-all cursor-crosshair">
            <BrandMark className="w-6 h-6" />
            <span className="text-[10px] text-ink-muted font-black uppercase tracking-[0.4em]">MindMaple Core Architecture</span>
          </div>
          <div className="px-6 py-2.5 bg-white rounded-full border border-paper-dark shadow-sm">
            <span className="text-[10px] text-ink-muted font-black uppercase tracking-[0.2em]">
              Made by LingLink AI and DigLab AI
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoView;